import {
  Body,
  Button,
  CardBackground,
  ColumnHeaderCell,
  Header,
  Row,
  Table,
  Typography,
  useToast,
} from "@hellocarbo/lasagne";
import { Plus } from "lucide-react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  ActionFunctionArgs,
  Link,
  useActionData,
  useLoaderData,
  useNavigation,
  useSubmit,
} from "react-router";
import invariant from "tiny-invariant";

import {
  AdditionalModules,
  EmptyView,
  SimulationRow,
} from "@/components/Simulation";
import PageWrapper from "@/components/ui/PageWrapper";
import { TSimulation } from "@/types";

export async function loader() {
  const response = await fetch("/api/client/simulations");

  if (response.status !== 200) {
    return { ok: false };
  }

  const { data: simulations } = (await response.json()) as {
    data: Array<TSimulation>;
  };

  return { ok: true, simulations };
}

export async function action({ request }: ActionFunctionArgs) {
  const formData = await request.formData();
  const simulationId = await formData.get("simulationId");

  invariant(simulationId, "No simulation id found.");

  const response = await fetch(`/api/client/simulations/${simulationId}`, {
    headers: { "Content-Type": "application/json" },
    method: "DELETE",
  });

  if (response.status !== 204) {
    return { ok: false };
  }

  return { ok: true };
}

function Simulations() {
  const { simulations } = useLoaderData<typeof loader>();
  const actionData = useActionData<typeof action>();
  const navigation = useNavigation();

  const { t } = useTranslation("simulation");
  const { toast } = useToast();
  const submit = useSubmit();

  useEffect(() => {
    if (navigation.state === "idle" && actionData?.ok) {
      toast({
        title: t("simulation:simulationDeleted"),
        intent: "positive",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigation.state, actionData]);

  return (
    <PageWrapper>
      <div className="mx-xl my-lg">
        <Typography step="3" as="h1" weight="bold" className="mb-sm">
          {t("simulation:carbonCalculator")}
        </Typography>
        {simulations && simulations.length > 0 ? (
          <div className="gap-y-sm flex flex-col">
            <CardBackground>
              <Button
                intent="positive"
                shape="soft"
                size="small"
                as={Link}
                to="/simulations/scenarios"
              >
                <Plus className="size-sm" />
                {t("simulation:newSimulation")}
              </Button>
              <Table className="mt-md">
                <Header>
                  <Row>
                    <ColumnHeaderCell>
                      {t("simulation:simulations")}
                    </ColumnHeaderCell>
                    <ColumnHeaderCell>
                      {t("simulation:employeesNumber")}
                    </ColumnHeaderCell>
                    <ColumnHeaderCell>
                      {t("simulation:emissions")}
                    </ColumnHeaderCell>
                    <ColumnHeaderCell className="text-right">
                      {t("simulation:actions")}
                    </ColumnHeaderCell>
                  </Row>
                </Header>
                <Body>
                  {simulations.map((simulation) => (
                    <SimulationRow
                      key={simulation.id}
                      simulation={simulation}
                      deleteSimulation={() =>
                        submit(
                          { simulationId: simulation.id },
                          { method: "DELETE" }
                        )
                      }
                    />
                  ))}
                </Body>
              </Table>
            </CardBackground>
            <AdditionalModules />
          </div>
        ) : (
          <EmptyView />
        )}
      </div>
    </PageWrapper>
  );
}

export default Simulations;
